/**
 * pricingEducationPlans.ts
 *
 * Builds the AI Bootcamp cards on /pricing from GET /checkout/catalog.
 *
 * WHY THIS EXISTS:
 * - Until 2026-10-14 the pricing page showed "See course pricing" for the live
 *   Bootcamp and a typed-in $499 / $997 for self-paced, under the monthly plans.
 *   The Bootcamp is the product that brings in the most money per buyer, so
 *   Rob moved it above the plans and asked for real prices on it.
 * - Every price, payment plan, session count, start date, guarantee length and
 *   included Pro days comes from the catalog (product, product_plan, promotions
 *   and product.metadata). Nothing here hardcodes a number.
 *
 * NO FALLBACKS: when the catalog cannot supply a Bootcamp product or its prices,
 * the builder throws. The page then shows an unavailable state for the Bootcamp
 * section and no buy button, never a stale price.
 */
import type { PricingPlan } from './PricingPageExperiment';

export const LIVE_BOOTCAMP_SLUG = 'ai-bootcamp';
export const SELF_PACED_BOOTCAMP_SLUG = 'ai-bootcamp-self-paced';

type CatalogPlan = {
  planKey: string;
  label?: string | null;
  amount?: number | null;
  discountedAmount?: number | null;
  stripePriceId?: string | null;
  metadata?: { installments?: number } | null;
  checkoutAvailability?: { purchasable?: boolean; status?: string; message?: string | null };
};

/** Bootcamp fields Node publishes in product.metadata (see Node checkoutCatalog.service). */
type BootcampCohort = {
  startsAt?: string | null;
  timezone?: string | null;
  sessionCount?: number | null;
};

type BootcampMetadata = {
  cohort?: BootcampCohort | null;
  includedProDays?: number | null;
  guaranteeDays?: number | null;
  priceAnchorCents?: number | null;
};

type CatalogProduct = {
  slug: string;
  metadata?: BootcampMetadata | null;
  plans?: CatalogPlan[];
};

type Catalog = { products?: CatalogProduct[] } | null | undefined;

type EducationFeature = { label: string; detail?: string };

/** Copy the education tiers already own in publicPricingSource. */
export type EducationTierCopy = {
  id: string;
  name: string;
  tagline: string;
  bestFor?: string;
  cta: string;
  features: EducationFeature[];
};

export type EducationPricingPlan = PricingPlan & {
  type: 'course';
  /** "/mo" for an installment headline, "one-time" for a single payment. */
  priceSuffix: string;
  /** One line under the price, e.g. "5 monthly payments ($3,485 total) or $2,999 paid in full". */
  priceNote?: string;
  /** Short facts read from catalog metadata, shown before the copy bullets. */
  facts: string[];
  checkoutClosedMessage?: string;
};

const usd = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: cents % 100 ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;

/**
 * Per-payment charge, list amount and total for one catalog plan. The same
 * arithmetic the course page uses: the discounted amount wins when present, and
 * an installment plan's total is the per-payment charge times its payments.
 */
function planView(plan: CatalogPlan) {
  const amount = Number(plan.amount);
  const discounted =
    Number.isSafeInteger(plan.discountedAmount) && Number(plan.discountedAmount) < amount
      ? Number(plan.discountedAmount)
      : null;
  const charge = discounted ?? amount;
  const installments = positiveInt(plan.metadata?.installments) ?? 1;
  return {
    installments,
    amount: charge,
    originalAmount: amount,
    hasDiscount: discounted !== null,
    totalDisplay: usd(charge * installments),
  };
}

const positiveInt = (value: number | null | undefined): number | null => {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
};

function findProduct(catalog: Catalog, slug: string): CatalogProduct {
  const product = (catalog?.products || []).find((p) => p?.slug === slug);
  if (!product) throw new Error(`Commercial catalog is missing ${slug}`);
  return product;
}

function closedMessage(plan: CatalogPlan): string | undefined {
  const availability = plan.checkoutAvailability;
  if (!availability || availability.purchasable !== false) return undefined;
  return availability.message || 'Enrollment is closed right now.';
}

/** "Wed, Oct 28" in the cohort's own time zone. */
function cohortStartLabel(cohort: BootcampCohort | null | undefined): string | null {
  const startsAt = cohort?.startsAt ? new Date(cohort.startsAt) : null;
  if (!startsAt || Number.isNaN(startsAt.getTime())) return null;
  const timeZone = cohort?.timezone || 'America/New_York';
  return startsAt.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone,
  });
}

/** Copy bullets that restate a catalog fact are replaced by the catalog fact. */
const CATALOG_OWNED_COPY = /launch price|days of pro access|money-back/i;

// These buttons start checkout directly, so their labels name the purchase.
// Interim: the stored pricing card (product.metadata.pricingCard) replaces
// these labels once the Node seed is live.
const CHECKOUT_CTA = {
  live: 'Enroll in Live Bootcamp',
  selfPaced: 'Start Self-Paced',
} as const;

function copyFeatures(copy: EducationTierCopy) {
  return copy.features.filter((feature) => !CATALOG_OWNED_COPY.test(feature.label));
}

export function buildLiveBootcampPlan(
  catalog: Catalog,
  copy: EducationTierCopy,
): EducationPricingPlan {
  const product = findProduct(catalog, LIVE_BOOTCAMP_SLUG);
  const purchasable = (product.plans || []).filter(
    (plan) => plan.stripePriceId && Number.isSafeInteger(plan.amount),
  );
  if (!purchasable.length) throw new Error('Commercial catalog has no live Bootcamp plans');

  // Same per-payment and total math the course page uses (useCourseCatalog).
  const plans = purchasable.map((plan) => ({ raw: plan, view: planView(plan) }));
  const payInFull = plans.find((p) => p.view.installments === 1);
  // Smallest payment leads (Rob, 2026-09-13): the most installments wins.
  const headline = [...plans].sort((a, b) => b.view.installments - a.view.installments)[0];

  const isInstallment = headline.view.installments > 1;
  const noteParts: string[] = [];
  if (isInstallment) {
    noteParts.push(
      `${headline.view.installments} monthly payments (${headline.view.totalDisplay} total)`,
    );
    if (payInFull) noteParts.push(`or ${usd(payInFull.view.amount)} paid in full`);
  }

  const metadata: BootcampMetadata = product.metadata || {};
  const cohort = metadata.cohort;
  const sessionCount = positiveInt(cohort?.sessionCount);
  const startLabel = cohortStartLabel(cohort);
  const proDays = positiveInt(metadata.includedProDays);
  const guaranteeDays = positiveInt(metadata.guaranteeDays);

  const facts: string[] = [];
  if (sessionCount && startLabel)
    facts.push(`${sessionCount} live sessions, starting ${startLabel}`);
  else if (startLabel) facts.push(`Next class starts ${startLabel}`);
  if (proDays) facts.push(`${proDays} days of BotSpot Pro included`);
  if (guaranteeDays) facts.push(`${guaranteeDays}-day money-back guarantee`);

  return {
    id: copy.id,
    type: 'course',
    name: copy.name,
    tagline: copy.tagline,
    bestFor: copy.bestFor,
    cta: CHECKOUT_CTA.live,
    features: copyFeatures(copy),
    facts,
    price: headline.view.amount / 100,
    originalPrice: headline.view.hasDiscount ? headline.view.originalAmount / 100 : undefined,
    priceSuffix: isInstallment ? '/mo' : 'one-time',
    priceNote: noteParts.join(' ') || undefined,
    productSlug: product.slug,
    planKey: headline.raw.planKey,
    useCartCheckout: true,
    checkoutClosedMessage: closedMessage(headline.raw),
  };
}

export function buildSelfPacedBootcampPlan(
  catalog: Catalog,
  copy: EducationTierCopy,
): EducationPricingPlan {
  const product = findProduct(catalog, SELF_PACED_BOOTCAMP_SLUG);
  const plan = (product.plans || []).find(
    (candidate) => candidate.stripePriceId && Number.isSafeInteger(candidate.amount),
  );
  if (!plan) throw new Error('Commercial catalog has no self-paced Bootcamp plan');

  const view = planView(plan);
  const metadata: BootcampMetadata = product.metadata || {};
  const anchorCents = positiveInt(metadata.priceAnchorCents);
  const chargeCents = Number(view.amount);
  // The list price is the plan's own pre-promotion amount when a promotion
  // applies, otherwise the product's recorded anchor price.
  const listCents = view.hasDiscount
    ? Number(view.originalAmount)
    : anchorCents && anchorCents > chargeCents
      ? anchorCents
      : null;
  const proDays = positiveInt(metadata.includedProDays);

  const facts: string[] = [];
  if (proDays) facts.push(`${proDays} days of BotSpot Pro included`);

  return {
    id: copy.id,
    type: 'course',
    name: copy.name,
    tagline: copy.tagline,
    bestFor: copy.bestFor,
    cta: CHECKOUT_CTA.selfPaced,
    features: copyFeatures(copy),
    facts,
    price: chargeCents / 100,
    originalPrice: listCents ? listCents / 100 : undefined,
    discountPercent: listCents ? Math.round((1 - chargeCents / listCents) * 100) : undefined,
    priceSuffix: 'one-time',
    productSlug: product.slug,
    planKey: plan.planKey,
    useCartCheckout: true,
    checkoutClosedMessage: closedMessage(plan),
  };
}

/**
 * Live first, then self-paced. Throws when either product cannot be priced
 * from the catalog; callers render the Bootcamp unavailable state.
 */
export function buildEducationPricingPlans(
  catalog: Catalog,
  copies: EducationTierCopy[],
): EducationPricingPlan[] {
  const live = copies.find((c) => c.id === 'bootcamp-live');
  const selfPaced = copies.find((c) => c.id === 'bootcamp-self-paced');
  if (!live || !selfPaced) throw new Error('Bootcamp pricing copy is missing');
  return [buildLiveBootcampPlan(catalog, live), buildSelfPacedBootcampPlan(catalog, selfPaced)];
}
